import React, { useMemo } from 'react';
import { batch } from 'react-redux';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@apollo/client';
import useUser from '../../../../utils/hooks/useUser';
import {
  DefaultView,
  defaultViewLabels,
  isDefaultViewMember,
} from '../../../../utils/userSettings';
import {
  FieldLabel,
  FormatValueDropdown,
  VerticalField,
} from '../../../../components/Form';
import { assertNotNil, IReactSelectOption } from '../../../../utils/types';
import { useBootstrapSelector } from '../../../../redux/selectorHooks';
import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import {
  IUserSettingUpdateMutation,
  IUserSettingUpdateMutationVariables,
  UserSettingKind,
} from '../../../../utils/apollo/graphqlSchema';
import { USER_SETTING_UPDATE } from '../../../../utils/apollo/userSettings';
import toastMessage from '../../../../utils/toastMessage';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import {
  setDefaultSchemaId,
  setDefaultView,
} from '../../../../redux/slices/userSettings';
import { useAppDispatch } from '../../../../redux/hooks';
import { defaultUserSettingsSchema, IDefaultUserSettingsState } from './types';
import { PreferencesSection } from '../PreferencesUI';
import { updateUserFields } from '../../../../redux/slices/permission';

const FieldWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(120px, 240px);
  gap: 24px;
`;
const ActionsWrapper = styled.div`
  display: flex;
  column-gap: 16px;
`;

const viewOptions: IReactSelectOption[] = Object.values(DefaultView).map(
  (view) => ({
    value: view,
    label: defaultViewLabels[view],
  }),
);

const DefaultUserSettings = () => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const { defaultSchemaSetting, defaultViewSetting } = user;

  const schemaConfig = useBootstrapSelector('schemaConfig');
  assertNotNil(schemaConfig);
  const { schemas } = schemaConfig;

  const [updateUserSettings] = useMutation<
    IUserSettingUpdateMutation,
    IUserSettingUpdateMutationVariables
  >(USER_SETTING_UPDATE);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isDirty },
    reset,
  } = useForm<IDefaultUserSettingsState>({
    resolver: zodResolver(defaultUserSettingsSchema),
    defaultValues: {
      defaultSchemaId:
        (defaultSchemaSetting?.value ?? '') ||
        (schemaConfig?.defaultSchemaId ?? ''),
      defaultView: isDefaultViewMember(defaultViewSetting?.value)
        ? defaultViewSetting?.value
        : DefaultView.StandardView,
    },
  });

  const onSubmitForm = handleSubmit(async (formData) => {
    const { defaultView, defaultSchemaId } = formData;

    const response = await updateUserSettings({
      variables: {
        input: {
          userSettings: [
            {
              kind: UserSettingKind.DefaultSchema,
              value: defaultSchemaId,
            },
            {
              kind: UserSettingKind.DefaultView,
              value: defaultView,
            },
          ],
        },
      },
    });

    if (printGraphqlErrors(response?.data?.userSettingUpdate?.errors)) {
      return;
    }

    batch(() => {
      dispatch(setDefaultSchemaId(defaultSchemaId));
      dispatch(setDefaultView(defaultView));
      dispatch(
        updateUserFields({
          user: {
            ...user,
            defaultSchemaSetting: {
              __typename: 'UserSetting',
              kind: UserSettingKind.DefaultSchema,
              value: defaultSchemaId,
            },
            defaultViewSetting: {
              __typename: 'UserSetting',
              kind: UserSettingKind.DefaultView,
              value: defaultView,
            },
          },
        }),
      );
    });

    toastMessage({
      type: 'info',
      message: 'User Settings were updated',
    });

    reset(formData);
  });

  const handleReset = () => {
    reset();
  };

  const schemaOptions: IReactSelectOption[] = useMemo(
    () =>
      schemas.map((schema) => ({
        label: schema.name,
        value: schema.id,
      })),
    [schemas],
  );

  return (
    <PreferencesSection>
      <form onSubmit={onSubmitForm}>
        <FieldWrapper>
          <Controller
            name="defaultSchemaId"
            control={control}
            render={({ field, fieldState }) => (
              <VerticalField>
                <FieldLabel>Default Schema</FieldLabel>
                <FormatValueDropdown
                  value={field.value}
                  onChange={field.onChange}
                  options={schemaOptions}
                  placeholder={'Select Schema'}
                  hasError={Boolean(fieldState.error)}
                />
              </VerticalField>
            )}
          />
          <Controller
            name="defaultView"
            control={control}
            render={({ field, fieldState }) => (
              <VerticalField>
                <FieldLabel>Default View</FieldLabel>
                <FormatValueDropdown
                  value={field.value}
                  onChange={field.onChange}
                  options={viewOptions}
                  placeholder={'Select View'}
                  hasError={Boolean(fieldState.error)}
                />
              </VerticalField>
            )}
          />
        </FieldWrapper>
        <ActionsWrapper>
          <PrimaryButton
            type="submit"
            disabled={isSubmitting || !isDirty}
            trackContext="settings"
            trackAction="preferences - save"
          >
            Save
          </PrimaryButton>
          <SecondaryButton
            type="button"
            onClick={handleReset}
            disabled={isSubmitting || !isDirty}
            isOutlined={true}
            trackContext="settings"
            trackAction="preferences - reset"
          >
            Reset
          </SecondaryButton>
        </ActionsWrapper>
      </form>
    </PreferencesSection>
  );
};

export default DefaultUserSettings;
